import './../../Player.css';
import React, {useContext, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import { printObj, isIOS, getIOSVersion } from '../../Utils';
import FadeInPanel from '../components/FadeInPanel';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import {
    geocodeAddressQuery,
    getPlaceMapsQuery,
    hasChainRestaurants,
    openGoogleMapsSearch,
    resolveRestaurantMapTarget,
} from '../../chainRestaurantUtils';
import * as Sentry from "@sentry/react";
import LocationHeaderCard from './LocationHeaderCard';

function LocationButton(props){
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);
    const [locationPanelKey, setLocationPanelKey] = useState(0);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [headerInfo, setHeaderInfo] = useState({ title: '', address: '' });

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const primaryColor = useContext(PlayerContext).get("primaryColor") || '#2FC4B5';
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const idPrefix = fromDemo ? (fromDemo +"-") : "";
    const mapElementId = idPrefix + "location-map";

    const canShowLocation = props.story?.posId != null || hasChainRestaurants(props.story);
    const shouldOpenMap = activeFeature === "location" && props.story?.posId != null;

    useEffect(() => {
        if (shouldOpenMap) {
            const fromData = resolveRestaurantMapTarget(props.story, props.placeDetails);
            setHeaderInfo({
                title: fromData?.title || props.story?.posName || '',
                address: fromData?.address || props.story?.posArea || props.story?.formattedAddress || '',
            });
            setIsOpened(true);
            setLocationPanelKey((key) => key + 1);
            setMapLoaded(false);
            return;
        }
        if (activeFeature !== "location") {
            setIsOpened(false);
            setMapLoaded(false);
        }
    }, [shouldOpenMap, activeFeature, props.story?.posId, props.story, props.placeDetails]);

    useEffect(() => {
        if (!mapLoaded && props.show && isOpened && shouldOpenMap) {
            initMap();
        }
    }, [props.show, isOpened, shouldOpenMap, locationPanelKey, mapLoaded]);

    function resolveMapsOpenQuery(restaurant) {
        return getPlaceMapsQuery(props.story)
            || [restaurant?.title, restaurant?.address].filter(Boolean).join(', ')
            || props.story?.posName
            || props.story?.posArea
            || props.story?.formattedAddress
            || '';
    }

    function buildMapsInfoWindowContent(restaurant) {
        const sm = sizeMultiplier;
        const container = document.createElement('div');
        container.style.fontFamily = 'sans-serif';
        container.style.fontSize = `${14 * sm}px`;
        container.style.lineHeight = '1.4';
        container.style.maxWidth = `${Math.max(160, Math.round(200 * sm))}px`;
        container.style.boxSizing = 'border-box';
        container.style.padding = `${4 * sm}px ${2 * sm}px`;

        if (restaurant?.title) {
            const title = document.createElement('strong');
            title.textContent = restaurant.title;
            title.style.fontSize = `${14 * sm}px`;
            container.appendChild(title);
        }

        if (restaurant?.address) {
            const address = document.createElement('div');
            address.style.marginTop = `${4 * sm}px`;
            address.style.fontSize = `${12 * sm}px`;
            address.textContent = restaurant.address;
            container.appendChild(address);
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = t('OpenOnGoogleMaps');
        button.style.marginTop = `${8 * sm}px`;
        button.style.padding = `${4 * sm}px 0`;
        button.style.border = 'none';
        button.style.backgroundColor = 'transparent';
        button.style.color = primaryColor;
        button.style.cursor = 'pointer';
        button.style.fontSize = `${13 * sm}px`;
        button.style.fontWeight = '600';
        button.style.textAlign = 'left';
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            openGoogleMapsSearch(resolveMapsOpenQuery(restaurant));
        });
        container.appendChild(button);

        return container;
    }

    async function resolveMarkerTarget() {
        const fromData = resolveRestaurantMapTarget(props.story, props.placeDetails);
        if (fromData?.position) {
            return fromData;
        }

        const query = getPlaceMapsQuery(props.story)
            || props.story?.posArea
            || props.story?.formattedAddress
            || props.placeDetails?.formattedAddress
            || '';
        const geocoded = await geocodeAddressQuery(query);
        if (geocoded) {
            return {
                title: props.story?.posName || '',
                address: props.story?.posArea || props.story?.formattedAddress || query,
                position: geocoded,
            };
        }
        return null;
    }

    async function initMap() {
        if (isIOS() && getIOSVersion() && getIOSVersion()[0] <= 13) {
            fallback();
            return;
        }

        try {
            const restaurant = await resolveMarkerTarget();
            if (!restaurant?.position) {
                fallback();
                return;
            }

            setHeaderInfo({
                title: restaurant.title || '',
                address: restaurant.address || '',
            });

            const mapEl = document.getElementById(mapElementId);
            if (!mapEl) {
                return;
            }

            const { Map, InfoWindow } = await window.google.maps.importLibrary("maps");
            const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary("marker");

            const map = new Map(mapEl, {
                zoom: 13,
                minZoom: 7,
                maxZoom: 20,
                center: restaurant.position,
                mapId: "DEMO_MAP_ID",
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                zoomControl: false,
                cameraControl: false,
                clickableIcons: false,
            });

            const pinElement = new PinElement({ scale: 1.4 });
            const marker = new AdvancedMarkerElement({
                map,
                position: restaurant.position,
                content: pinElement.element,
                title: restaurant.title,
                gmpClickable: true,
            });

            const infoWindow = new InfoWindow({
                maxWidth: Math.max(180, Math.round(220 * sizeMultiplier)),
            });
            marker.addListener('click', () => {
                infoWindow.setContent(buildMapsInfoWindowContent(restaurant));
                infoWindow.open({ map, anchor: marker });
            });

            setMapLoaded(true);
        } catch (e) {
            Sentry.captureMessage("Location map init failed: " + printObj(e));
            fallback();
        }
    }

    function fallback() {
        const query = resolveMapsOpenQuery({
            title: props.story?.posName,
            address: props.story?.posArea || props.story?.formattedAddress,
        });
        propagateFocusChange(true);
        if (query) {
            openGoogleMapsSearch(query);
        }
    }

    function showCloseButton() {
        return (
            <div style={{
                position: 'absolute',
                bottom: 16 * sizeMultiplier,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 40
            }}>
                <IconButton
                    onClick={() => propagateFocusChange(true)}
                    style={{
                        background: "#fff",
                        borderRadius: "50%",
                        border: `1px solid #b0b0b0`,
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CloseIcon style={{ color: "#808080", fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier }} />
                </IconButton>
            </div>
        );
    }

    if (!canShowLocation) {
        return null;
    }

    const locationOpen = props.show && isOpened && shouldOpenMap;

    return (
        <div style={{ width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, position: 'absolute' }}>
            <FadeInPanel
                className="location-container"
                animationKey={locationOpen ? `location-${locationPanelKey}` : 'location-closed'}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: storyDimensions.containerWidth,
                    height: storyDimensions.containerHeight,
                    pointerEvents: locationOpen ? 'auto' : 'none',
                    zIndex: 20,
                    visibility: locationOpen ? 'visible' : 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    id={idPrefix + "location-overlay"}
                    className="BlockingOverlay"
                    style={{
                        width: storyDimensions.containerWidth,
                        height: storyDimensions.containerHeight,
                        pointerEvents: 'auto',
                    }}
                    onClick={() => propagateFocusChange(true)}
                />
                <div
                    id={mapElementId}
                    onClick={(e) => { e.stopPropagation(); }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 20,
                        width: storyDimensions.containerWidth,
                        height: storyDimensions.containerHeight,
                        borderRadius: 0,
                    }}
                />
                {locationOpen && (
                    <LocationHeaderCard
                        story={props.story}
                        title={headerInfo.title}
                        address={headerInfo.address}
                        sizeMultiplier={sizeMultiplier}
                    />
                )}
                {locationOpen && showCloseButton()}
            </FadeInPanel>
        </div>
    );
}

export default LocationButton;
